/**
 * ============================================================================
 * MASTER PAGE: Messaging Limit & Queue Management (`tbl_user_master`)
 * ============================================================================
 * Purpose:
 *   Dedicated, full-page management screen for tenant RabbitMQ queue configuration,
 *   24-hour messaging limits, per-minute rate throttling, dynamic worker pipelines,
 *   and queue lifecycle actions (Pause, Resume, Drain, Retry, Restart, Clear).
 *
 * Route:
 *   /admin/users/queue/:id or /admin/users/:id/queue
 * ============================================================================
 */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import Swal from "sweetalert2";
import { userService, accountService, roleService } from "../../services";
import { getCurrentUser, isSuperAdmin, isRegularUser } from "../../../core/utils/auth";

// SVG Icons
const ChevronLeftIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
  </svg>
);

const ArrowPathIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
  </svg>
);

const ServerStackIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 14.25h13.5m-13.5 3h13.5m-13.5 3h13.5M6 20.25a2.25 2.25 0 1 1 0-4.5 2.25 2.25 0 0 1 0 4.5Zm0-3a2.25 2.25 0 1 1 0-4.5 2.25 2.25 0 0 1 0 4.5Zm0-3a2.25 2.25 0 1 1 0-4.5 2.25 2.25 0 0 1 0 4.5ZM3.75 6A2.25 2.25 0 0 1 6 3.75h12A2.25 2.25 0 0 1 20.25 6v12A2.25 2.25 0 0 1 18 20.25H6A2.25 2.25 0 0 1 3.75 18V6Z" />
  </svg>
);

const BoltIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="m3.75 13.5 10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75Z" />
  </svg>
);

const ClipboardDocumentIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.666 3.888A2.25 2.25 0 0 0 13.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 0 1-.75.75H9a.75.75 0 0 1-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 0 1-2.25 2.25H6.75A2.25 2.25 0 0 1 4.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 0 1 1.927-.184" />
  </svg>
);

const PauseIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25v13.5m-7.5-13.5v13.5" />
  </svg>
);

const PlayIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.347a1.125 1.125 0 0 1 0 1.972l-11.54 6.347a1.125 1.125 0 0 1-1.667-.986V5.653Z" />
  </svg>
);

const TrashIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
  </svg>
);

const UserCircleIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M17.982 18.725A7.488 7.488 0 0 0 12 15.75a7.488 7.488 0 0 0-5.982 2.975m11.963 0a9 9 0 1 0-11.963 0m11.963 0A8.966 8.966 0 0 1 12 21a8.966 8.966 0 0 1-5.982-2.275M15 9.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
  </svg>
);

const CheckCircleIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
  </svg>
);

export default function UserQueueManagement() {
  const navigate = useNavigate();
  const { id } = useParams();

  const loggedUser = getCurrentUser();
  const isSuper = isSuperAdmin(loggedUser);
  const isClient = isRegularUser(loggedUser);

  // Users & Accounts directory for switching
  const [usersList, setUsersList] = useState([]);
  const [clientAccounts, setClientAccounts] = useState([]);
  const [roles, setRoles] = useState([]);

  // Active Selected User
  const [selectedUserId, setSelectedUserId] = useState(id || "");
  const [currentUserData, setCurrentUserData] = useState(null);

  // Queue Settings & Diagnostics
  const [queueSettings, setQueueSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncingQueue, setSyncingQueue] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [copiedQueue, setCopiedQueue] = useState(false);
  const [activeTab, setActiveTab] = useState("queue"); // "queue" | "limits" | "worker" | "usage" | "general"

  // Load Metadata (Users, Accounts, Roles)
  const loadMetadata = useCallback(async () => {
    try {
      const [uRes, aRes, rRes] = await Promise.all([
        userService.get({ limit: 500 }).catch(() => ({ data: [] })),
        accountService.get({ limit: 500 }).catch(() => ({ data: [] })),
        roleService.get({ limit: 100 }).catch(() => ({ data: [] })),
      ]);

      const rawUsers = Array.isArray(uRes?.data) ? uRes.data : Array.isArray(uRes?.data?.data) ? uRes.data.data : [];
      const userArray = Array.isArray(rawUsers) ? rawUsers : [];
      setUsersList(userArray);

      const rawAccounts = Array.isArray(aRes?.data) ? aRes.data : Array.isArray(aRes?.data?.data) ? aRes.data.data : [];
      setClientAccounts(Array.isArray(rawAccounts) ? rawAccounts : []);

      const rawRoles = Array.isArray(rRes?.data) ? rRes.data : Array.isArray(rRes?.data?.data) ? rRes.data.data : [];
      setRoles(Array.isArray(rawRoles) ? rawRoles : []);

      // If no ID passed in URL, pick the first user or logged-in user
      if (!selectedUserId && userArray.length > 0) {
        const defaultUser = userArray.find((u) => u._id === loggedUser?._id) || userArray[0];
        setSelectedUserId(defaultUser._id || defaultUser.str_username);
      }
    } catch (err) {
      console.error("Failed to load metadata:", err);
    }
  }, [selectedUserId, loggedUser]);

  useEffect(() => {
    loadMetadata();
  }, [loadMetadata]);

  // Load Queue Settings when selectedUserId changes
  const loadQueueSettings = useCallback(async (targetUserId) => {
    if (!targetUserId) return;
    setLoading(true);
    setCopiedQueue(false);
    try {
      const res = await userService.getQueueSettings(targetUserId);
      if (res?.success && res.data) {
        setQueueSettings(res.data);
        if (res.data.user) {
          setCurrentUserData(res.data.user);
        }
      } else if (res?.data) {
        setQueueSettings(res.data);
      }

      // Also fetch user details if not yet in state
      const uRes = await userService.view(targetUserId).catch(() => null);
      if (uRes?.data) {
        setCurrentUserData(uRes.data);
      }
    } catch (err) {
      console.error("Failed to load user queue settings:", err);
      Swal.fire({
        title: "Load Error",
        text: err.response?.data?.error || err.message || "Could not retrieve user queue configuration.",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedUserId) {
      loadQueueSettings(selectedUserId);
    }
  }, [selectedUserId, loadQueueSettings]);

  // Sync selected user when URL parameter changes
  useEffect(() => {
    if (id && id !== selectedUserId) {
      setSelectedUserId(id);
    }
  }, [id, selectedUserId]);

  // Resolve target user object from list or state
  const targetUser = useMemo(() => {
    if (!selectedUserId && !queueSettings) return null;
    const found = usersList.find(
      (u) =>
        String(u._id) === String(selectedUserId) ||
        String(u.str_username) === String(selectedUserId) ||
        String(u.int_user_id) === String(selectedUserId)
    );
    if (found) return found;
    if (currentUserData) return currentUserData;
    if (queueSettings?.user) return queueSettings.user;
    if (queueSettings?.str_username) {
      return {
        _id: queueSettings.user_id || selectedUserId,
        int_user_id: queueSettings.int_user_id,
        str_username: queueSettings.str_username,
        str_name: queueSettings.str_name || queueSettings.str_username,
        str_account_id: queueSettings.str_account_id,
        str_status: queueSettings.queue_status || "Active",
      };
    }
    return null;
  }, [selectedUserId, usersList, currentUserData, queueSettings]);

  // Account & Role Details
  const userAccount = useMemo(() => {
    if (!targetUser?.str_account_id) return null;
    return clientAccounts.find(
      (a) =>
        String(a.int_account_id) === String(targetUser.str_account_id) ||
        String(a._id) === String(targetUser.str_account_id)
    );
  }, [targetUser, clientAccounts]);

  const userRole = useMemo(() => {
    if (!targetUser?.str_role_id) return null;
    return roles.find(
      (r) =>
        String(r.int_role_id) === String(targetUser.str_role_id) ||
        String(r._id) === String(targetUser.str_role_id)
    );
  }, [targetUser, roles]);

  // Save Settings
  const handleSaveQueueSettings = async () => {
    if (!targetUser || !queueSettings) return;
    setSaving(true);
    try {
      const payload = {
        num_24h_message_limit: queueSettings.limit_24h,
        num_min_messages_per_minute: queueSettings.min_per_minute,
        num_max_messages_per_minute: queueSettings.max_per_minute,
        num_messages_per_minute: queueSettings.per_minute_limit,
        str_queue_status: queueSettings.queue_status,
        obj_worker_config: queueSettings.worker_config,
      };

      const res = await userService.updateQueueSettings(
        targetUser._id || targetUser.str_username,
        payload
      );

      if (res?.success) {
        setQueueSettings(res.data);
        Swal.fire({
          title: "Settings Saved",
          text: `Messaging limits & queue configuration for @${targetUser.str_username} updated successfully.`,
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
        });
      }
    } catch (err) {
      Swal.fire({
        title: "Save Failed",
        text: err.response?.data?.error || err.message || "Failed to update queue settings.",
        icon: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  // Recreate / Sync RabbitMQ Queue
  const handleRecreateQueue = async () => {
    if (!targetUser) return;
    setSyncingQueue(true);
    try {
      const res = await userService.recreateQueue(targetUser._id || targetUser.str_username);
      if (res?.success) {
        setQueueSettings(res.data);
        Swal.fire({
          title: "Queue Synchronized",
          text: res.message || "RabbitMQ queue verified and operational.",
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
        });
      }
    } catch (err) {
      Swal.fire({
        title: "Sync Error",
        text: err.response?.data?.error || "Failed to synchronize RabbitMQ queue.",
        icon: "error",
      });
    } finally {
      setSyncingQueue(false);
    }
  };

  // Queue Lifecycle Action (PAUSE, RESUME, DRAIN, RETRY_FAILED, CLEAR_PENDING, RESTART_WORKERS)
  const handleQueueAction = async (action) => {
    if (!targetUser) return;
    const userId = targetUser._id || targetUser.str_username;

    if (action === "CLEAR_PENDING") {
      const confirmRes = await Swal.fire({
        title: "Clear Pending Jobs?",
        text: "Warning: This will permanently remove all un-dispatched campaign jobs queued in RabbitMQ for this user. In-flight messages will complete safely, but unsent messages will be dropped.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#ef4444",
        confirmButtonText: "Yes, Clear Jobs",
        cancelButtonText: "Cancel",
      });
      if (!confirmRes.isConfirmed) return;
    }

    setActionLoading(true);
    try {
      const res = await userService.executeQueueAction(userId, action);
      if (res?.success) {
        Swal.fire({
          title: "Action Executed",
          text: res.message || `Queue action "${action}" completed successfully.`,
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
        });
        // Refresh settings
        const updated = await userService.getQueueSettings(userId);
        if (updated?.success && updated.data) {
          setQueueSettings(updated.data);
        }
      }
    } catch (err) {
      Swal.fire({
        title: "Action Failed",
        text: err.response?.data?.error || err.message || `Failed to execute ${action}`,
        icon: "error",
      });
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50/50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 p-4 sm:p-6 lg:p-8 space-y-6">
      
      {/* ── Breadcrumb & Navigation Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mb-1">
            <Link to="/admin/users" className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">
              Users Management
            </Link>
            <span>/</span>
            <span className="text-gray-800 dark:text-gray-200 font-semibold">
              Messaging Limit & Queue Management
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate("/admin/users")}
              className="p-2 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 transition-colors shadow-2xs cursor-pointer"
              title="Back to Users List"
            >
              <ChevronLeftIcon className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl sm:text-2xl font-black text-gray-900 dark:text-white flex items-center gap-2.5">
                <ServerStackIcon className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
                <span>Messaging Limit & Queue Management</span>
              </h1>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                Configure dedicated RabbitMQ tenant queues, 24-hour rate limits, and worker pipelines.
              </p>
            </div>
          </div>
        </div>

        {/* Global Action Buttons */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => loadQueueSettings(selectedUserId)}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 text-xs font-semibold shadow-2xs transition-colors cursor-pointer disabled:opacity-50"
            title="Refresh Diagnostics"
          >
            <ArrowPathIcon className={`w-4 h-4 ${loading ? "animate-spin text-indigo-600" : ""}`} />
            <span>Refresh</span>
          </button>

          <button
            type="button"
            onClick={handleRecreateQueue}
            disabled={syncingQueue || !targetUser}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 text-indigo-600 dark:text-indigo-400 text-xs font-semibold shadow-2xs transition-colors cursor-pointer disabled:opacity-50"
            title="Verify or Re-create Queue on RabbitMQ Cluster"
          >
            <ArrowPathIcon className={`w-4 h-4 ${syncingQueue ? "animate-spin" : ""}`} />
            <span>Sync Queue</span>
          </button>

          <button
            type="button"
            onClick={handleSaveQueueSettings}
            disabled={saving || loading || !targetUser}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 text-white text-xs font-bold shadow-md shadow-indigo-500/20 transition-all cursor-pointer disabled:opacity-50"
          >
            {saving ? (
              <>
                <ArrowPathIcon className="w-4 h-4 animate-spin" />
                <span>Saving...</span>
              </>
            ) : (
              <>
                <CheckCircleIcon className="w-4 h-4" />
                <span>Save Queue Settings</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* ── User Switcher & Hero Identity Card ── */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-5 shadow-xs space-y-4">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          
          {/* User Profile Overview */}
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 via-blue-600 to-indigo-700 text-white flex items-center justify-center font-black text-xl shadow-md shadow-indigo-500/20 flex-shrink-0">
              {(targetUser?.str_name || targetUser?.str_username || "U").charAt(0).toUpperCase()}
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-lg font-black text-gray-900 dark:text-white">
                  {targetUser?.str_name || targetUser?.str_username || "Loading User..."}
                </h2>
                <code className="text-xs font-bold font-mono px-2 py-0.5 rounded-md bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-800">
                  @{targetUser?.str_username || "..."}
                </code>
                {userRole && (
                  <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-purple-50 dark:bg-purple-950/60 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-800">
                    {userRole.str_role_name}
                  </span>
                )}
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                  targetUser?.str_status === "Active" || targetUser?.bool_is_active
                    ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800"
                    : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                }`}>
                  {targetUser?.str_status || "Active"}
                </span>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-2 flex-wrap">
                <span>Account: <strong className="text-gray-700 dark:text-gray-300">{userAccount?.str_company_name || targetUser?.str_account_id || "Platform"}</strong></span>
                <span>•</span>
                <span>Scope: <code className="font-mono text-gray-600 dark:text-gray-400">digi_user_{targetUser?._id || "tenant"}</code></span>
                {queueSettings?.queue_name && (
                  <>
                    <span>•</span>
                    <span className="font-mono text-indigo-600 dark:text-indigo-400">Queue: {queueSettings.queue_name}</span>
                  </>
                )}
              </p>
            </div>
          </div>

          {/* User Selector Dropdown for Admins */}
          {!isClient && (
            <div className="flex items-center gap-3">
              <label htmlFor="user-selector" className="text-xs font-semibold text-gray-500 dark:text-gray-400 whitespace-nowrap">
                Manage User:
              </label>
              <select
                id="user-selector"
                value={selectedUserId}
                onChange={(e) => {
                  const newId = e.target.value;
                  setSelectedUserId(newId);
                  navigate(`/admin/users/queue/${newId}`, { replace: true });
                }}
                className="px-3 py-2 text-xs font-medium rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 min-w-[220px]"
              >
                {targetUser && !usersList.some((u) => String(u._id) === String(targetUser._id)) && (
                  <option value={targetUser._id || selectedUserId}>
                    {targetUser.str_name ? `${targetUser.str_name} (@${targetUser.str_username})` : (targetUser.str_username || selectedUserId)}
                  </option>
                )}
                {usersList.map((u) => (
                  <option key={u._id} value={u._id}>
                    {u.str_name ? `${u.str_name} (@${u.str_username})` : u.str_username}
                  </option>
                ))}
              </select>
            </div>
          )}

        </div>
      </div>

      {/* ── 5-Tab Navigation Bar ── */}
      <div className="flex items-center gap-2 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-2xl px-4 pt-2 overflow-x-auto shadow-2xs">
        {[
          { id: "queue", label: "Queue Diagnostics & Actions" },
          { id: "limits", label: "24h & Per-Minute Limits" },
          { id: "worker", label: "Worker Configuration" },
          { id: "usage", label: "Atomic Quota & Usage" },
          { id: "general", label: "General & Tenant Identity" },
        ].map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-3 text-xs font-bold rounded-t-xl transition-all border-b-2 cursor-pointer whitespace-nowrap ${
              activeTab === tab.id
                ? "text-indigo-600 dark:text-indigo-400 border-indigo-600 dark:border-indigo-400 bg-indigo-50/40 dark:bg-indigo-950/40"
                : "text-gray-500 dark:text-gray-400 border-transparent hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Main Tab Content ── */}
      <div className="bg-white dark:bg-gray-800 rounded-b-2xl border border-gray-200 dark:border-gray-700 p-6 shadow-xs min-h-[450px]">
        {loading ? (
          <div className="py-24 text-center">
            <ArrowPathIcon className="w-10 h-10 mx-auto text-indigo-500 animate-spin mb-3" />
            <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">
              Loading queue diagnostics & worker configuration...
            </p>
          </div>
        ) : !queueSettings ? (
          <div className="py-24 text-center text-gray-400">
            <ServerStackIcon className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-2" />
            <p className="text-sm">No queue configuration found for this user.</p>
          </div>
        ) : (
          <>
            {/* ══════════════════════════════════════════════════════════
                TAB 1: QUEUE DIAGNOSTICS & LIFECYCLE ACTIONS
            ══════════════════════════════════════════════════════════ */}
            {activeTab === "queue" && (
              <div className="space-y-6">
                
                {/* Active Tenant Queue Header Banner */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-gray-50 dark:bg-gray-900/60 rounded-2xl border border-gray-200 dark:border-gray-700 gap-3">
                  <div>
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">
                      Active Tenant Queue
                    </span>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-base font-mono font-black text-indigo-600 dark:text-indigo-400">
                        {queueSettings.queue_name}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard?.writeText(queueSettings.queue_name);
                          setCopiedQueue(true);
                          setTimeout(() => setCopiedQueue(false), 2000);
                        }}
                        className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded hover:bg-gray-200/60 dark:hover:bg-gray-800 transition-colors cursor-pointer"
                        title="Copy Queue Name"
                      >
                        <ClipboardDocumentIcon className="w-4 h-4" />
                      </button>
                      {copiedQueue && (
                        <span className="text-[11px] font-semibold text-emerald-600">Copied!</span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <span
                      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase border ${
                        queueSettings.queue_status === "ACTIVE"
                          ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800"
                          : "bg-amber-50 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300 border-amber-200 dark:border-amber-800"
                      }`}
                    >
                      <span
                        className={`w-2 h-2 rounded-full ${
                          queueSettings.queue_status === "ACTIVE" ? "bg-emerald-500 animate-pulse" : "bg-amber-500"
                        }`}
                      />
                      STATUS: {queueSettings.queue_status}
                    </span>
                  </div>
                </div>

                {/* 8 Live Pipeline Diagnostic Cards */}
                <div>
                  <h3 className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                    Live Queue Telemetry & Throughput
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="p-4 rounded-2xl bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700">
                      <span className="text-[10px] text-gray-400 font-semibold block uppercase">Queued Messages</span>
                      <p className="text-2xl font-black text-indigo-600 dark:text-indigo-400 font-mono mt-1">
                        {Number(queueSettings.queued_messages ?? queueSettings.current_queue_size ?? 0).toLocaleString()}
                      </p>
                    </div>

                    <div className="p-4 rounded-2xl bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700">
                      <span className="text-[10px] text-gray-400 font-semibold block uppercase">Processing</span>
                      <p className="text-2xl font-black text-amber-600 dark:text-amber-400 font-mono mt-1">
                        {Number(queueSettings.processing_messages || 0).toLocaleString()}
                      </p>
                    </div>

                    <div className="p-4 rounded-2xl bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700">
                      <span className="text-[10px] text-gray-400 font-semibold block uppercase">Completed</span>
                      <p className="text-2xl font-black text-emerald-600 dark:text-emerald-400 font-mono mt-1">
                        {Number(queueSettings.completed_messages || queueSettings.used_24h || 0).toLocaleString()}
                      </p>
                    </div>

                    <div className="p-4 rounded-2xl bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700">
                      <span className="text-[10px] text-gray-400 font-semibold block uppercase">Failed</span>
                      <p className="text-2xl font-black text-rose-600 dark:text-rose-400 font-mono mt-1">
                        {Number(queueSettings.failed_messages || 0).toLocaleString()}
                      </p>
                    </div>

                    <div className="p-4 rounded-2xl bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700">
                      <span className="text-[10px] text-gray-400 font-semibold block uppercase">Workers</span>
                      <p className="text-2xl font-black text-gray-800 dark:text-gray-200 font-mono mt-1">
                        {queueSettings.workers || queueSettings.active_workers || 3}
                      </p>
                    </div>

                    <div className="p-4 rounded-2xl bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700">
                      <span className="text-[10px] text-gray-400 font-semibold block uppercase">Active Workers</span>
                      <p className="text-2xl font-black text-blue-600 dark:text-blue-400 font-mono mt-1">
                        {queueSettings.active_workers || 3}
                      </p>
                    </div>

                    <div className="p-4 rounded-2xl bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700">
                      <span className="text-[10px] text-gray-400 font-semibold block uppercase">Current Rate</span>
                      <p className="text-2xl font-black text-purple-600 dark:text-purple-400 font-mono mt-1">
                        {queueSettings.current_rate || 0} <span className="text-xs font-normal text-gray-400">msg/min</span>
                      </p>
                    </div>

                    <div className="p-4 rounded-2xl bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700">
                      <span className="text-[10px] text-gray-400 font-semibold block uppercase">Configured Rate</span>
                      <p className="text-xl font-black text-gray-800 dark:text-gray-200 font-mono mt-1">
                        {queueSettings.min_per_minute || 40}–{queueSettings.max_per_minute || 60}{" "}
                        <span className="text-xs font-normal text-gray-400">msg/min</span>
                      </p>
                    </div>
                  </div>
                </div>

                {/* Queue Lifecycle Actions Panel */}
                <div className="p-5 rounded-2xl bg-gray-50/70 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-1.5">
                      <span>⚡ Queue Lifecycle Actions</span>
                    </h4>
                    {actionLoading && (
                      <span className="text-xs font-semibold text-indigo-600 dark:text-indigo-400 flex items-center gap-1.5">
                        <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" />
                        <span>Executing action...</span>
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {/* Pause Queue */}
                    <button
                      type="button"
                      onClick={() => handleQueueAction("PAUSE")}
                      disabled={actionLoading || queueSettings.queue_status === "PAUSED"}
                      className="p-3.5 rounded-xl border border-amber-200 dark:border-amber-900/50 bg-amber-50/60 dark:bg-amber-950/30 text-amber-800 dark:text-amber-300 hover:bg-amber-100/70 dark:hover:bg-amber-900/40 text-left transition-colors cursor-pointer disabled:opacity-40"
                    >
                      <div className="flex items-center gap-2 font-bold text-xs">
                        <PauseIcon className="w-4 h-4 text-amber-600" />
                        <span>Pause Queue</span>
                      </div>
                      <p className="text-[10px] text-amber-700/80 dark:text-amber-400 mt-1">
                        Hold dispatching without dropping messages
                      </p>
                    </button>

                    {/* Resume Queue */}
                    <button
                      type="button"
                      onClick={() => handleQueueAction("RESUME")}
                      disabled={actionLoading || queueSettings.queue_status === "ACTIVE"}
                      className="p-3.5 rounded-xl border border-emerald-200 dark:border-emerald-900/50 bg-emerald-50/60 dark:bg-emerald-950/30 text-emerald-800 dark:text-emerald-300 hover:bg-emerald-100/70 dark:hover:bg-emerald-900/40 text-left transition-colors cursor-pointer disabled:opacity-40"
                    >
                      <div className="flex items-center gap-2 font-bold text-xs">
                        <PlayIcon className="w-4 h-4 text-emerald-600" />
                        <span>Resume Queue</span>
                      </div>
                      <p className="text-[10px] text-emerald-700/80 dark:text-emerald-400 mt-1">
                        Resume worker consumption & dispatch
                      </p>
                    </button>

                    {/* Drain Queue */}
                    <button
                      type="button"
                      onClick={() => handleQueueAction("DRAIN")}
                      disabled={actionLoading}
                      className="p-3.5 rounded-xl border border-blue-200 dark:border-blue-900/50 bg-blue-50/60 dark:bg-blue-950/30 text-blue-800 dark:text-blue-300 hover:bg-blue-100/70 dark:hover:bg-blue-900/40 text-left transition-colors cursor-pointer disabled:opacity-40"
                    >
                      <div className="flex items-center gap-2 font-bold text-xs">
                        <ArrowPathIcon className="w-4 h-4 text-blue-600" />
                        <span>Drain Queue</span>
                      </div>
                      <p className="text-[10px] text-blue-700/80 dark:text-blue-400 mt-1">
                        Complete existing jobs and halt intake
                      </p>
                    </button>

                    {/* Retry Failed */}
                    <button
                      type="button"
                      onClick={() => handleQueueAction("RETRY_FAILED")}
                      disabled={actionLoading}
                      className="p-3.5 rounded-xl border border-purple-200 dark:border-purple-900/50 bg-purple-50/60 dark:bg-purple-950/30 text-purple-800 dark:text-purple-300 hover:bg-purple-100/70 dark:hover:bg-purple-900/40 text-left transition-colors cursor-pointer disabled:opacity-40"
                    >
                      <div className="flex items-center gap-2 font-bold text-xs">
                        <ArrowPathIcon className="w-4 h-4 text-purple-600" />
                        <span>Retry Failed</span>
                      </div>
                      <p className="text-[10px] text-purple-700/80 dark:text-purple-400 mt-1">
                        Re-queue failed broadcast jobs
                      </p>
                    </button>

                    {/* Restart Workers */}
                    <button
                      type="button"
                      onClick={() => handleQueueAction("RESTART_WORKERS")}
                      disabled={actionLoading}
                      className="p-3.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-800/80 text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 text-left transition-colors cursor-pointer disabled:opacity-40"
                    >
                      <div className="flex items-center gap-2 font-bold text-xs">
                        <ArrowPathIcon className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                        <span>Restart Workers</span>
                      </div>
                      <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-1">
                        Re-initialize consumer channels & prefetch
                      </p>
                    </button>

                    {/* Clear Pending Jobs */}
                    <button
                      type="button"
                      onClick={() => handleQueueAction("CLEAR_PENDING")}
                      disabled={actionLoading}
                      className="p-3.5 rounded-xl border border-rose-200 dark:border-rose-900/50 bg-rose-50/60 dark:bg-rose-950/30 text-rose-800 dark:text-rose-300 hover:bg-rose-100/70 dark:hover:bg-rose-900/40 text-left transition-colors cursor-pointer disabled:opacity-40"
                    >
                      <div className="flex items-center gap-2 font-bold text-xs">
                        <TrashIcon className="w-4 h-4 text-rose-600" />
                        <span>Clear Pending Jobs</span>
                      </div>
                      <p className="text-[10px] text-rose-700/80 dark:text-rose-400 mt-1">
                        Purge unsent jobs with confirmation
                      </p>
                    </button>
                  </div>
                </div>

              </div>
            )}

            {/* ══════════════════════════════════════════════════════════
                TAB 2: MESSAGING LIMIT (24h & Rate Bounds)
            ══════════════════════════════════════════════════════════ */}
            {activeTab === "limits" && (
              <div className="space-y-6">
                
                {/* 24-Hour Messaging Limit Tracking Banner */}
                <div className="p-5 rounded-2xl bg-gray-50/80 dark:bg-gray-900/60 border border-gray-200 dark:border-gray-700 space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-2">
                      <BoltIcon className="w-4 h-4 text-amber-500" />
                      <span>24-Hour Messaging Limit Tracking</span>
                    </span>
                    <span className="text-xs font-bold text-indigo-600 dark:text-indigo-400">
                      {queueSettings.is_unlimited ? "Unlimited Quota" : `${queueSettings.usage_percent}% Used`}
                    </span>
                  </div>

                  {/* Progress Track */}
                  {!queueSettings.is_unlimited && (
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
                      <div
                        className={`h-3 rounded-full transition-all duration-500 ${
                          queueSettings.usage_percent > 90
                            ? "bg-rose-500"
                            : queueSettings.usage_percent > 70
                            ? "bg-amber-500"
                            : "bg-gradient-to-r from-blue-500 to-indigo-600"
                        }`}
                        style={{ width: `${Math.min(100, Math.max(0, queueSettings.usage_percent))}%` }}
                      />
                    </div>
                  )}

                  {/* Summary row */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-1 text-center font-mono">
                    <div className="p-3 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                      <span className="text-[10px] text-gray-400 font-sans block">24 Hour Limit</span>
                      <strong className="text-base text-gray-900 dark:text-white font-black">
                        {queueSettings.is_unlimited ? "Unlimited" : Number(queueSettings.limit_24h || 0).toLocaleString()}
                      </strong>
                    </div>
                    <div className="p-3 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                      <span className="text-[10px] text-gray-400 font-sans block">Used (24h)</span>
                      <strong className="text-base text-blue-600 dark:text-blue-400 font-black">
                        {Number(queueSettings.used_24h || 0).toLocaleString()}
                      </strong>
                    </div>
                    <div className="p-3 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                      <span className="text-[10px] text-gray-400 font-sans block">Remaining</span>
                      <strong className="text-base text-emerald-600 dark:text-emerald-400 font-black">
                        {queueSettings.is_unlimited ? "∞" : Number(queueSettings.remaining_24h || 0).toLocaleString()}
                      </strong>
                    </div>
                    <div className="p-3 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                      <span className="text-[10px] text-gray-400 font-sans block">Usage</span>
                      <strong className="text-base text-indigo-600 dark:text-indigo-400 font-black">
                        {queueSettings.is_unlimited ? "0%" : `${queueSettings.usage_percent}%`}
                      </strong>
                    </div>
                  </div>
                </div>

                {/* 24-Hour Limit Presets */}
                <div>
                  <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-2">
                    24-Hour Messaging Quota Presets
                  </label>
                  <div className="grid grid-cols-3 sm:grid-cols-6 gap-2.5">
                    {[
                      { label: "250", value: 250 },
                      { label: "1K", value: 1000 },
                      { label: "2K", value: 2000 },
                      { label: "10K", value: 10000 },
                      { label: "100K", value: 100000 },
                      { label: "Unlimited", value: null },
                    ].map((preset) => {
                      const isSelected =
                        (preset.value === null && queueSettings.limit_24h === null) ||
                        (preset.value !== null && Number(queueSettings.limit_24h) === preset.value);
                      return (
                        <button
                          key={preset.label}
                          type="button"
                          onClick={() => {
                            setQueueSettings((prev) => ({
                              ...prev,
                              limit_24h: preset.value,
                              is_unlimited: preset.value === null,
                            }));
                          }}
                          className={`py-2.5 px-3 rounded-xl text-xs font-bold border transition-all cursor-pointer ${
                            isSelected
                              ? "bg-indigo-600 text-white border-indigo-600 shadow-md shadow-indigo-500/20"
                              : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
                          }`}
                        >
                          {preset.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Per-Minute Rate Range Controls */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div>
                    <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                      Messages Per Minute – MIN (Floor Rate)
                    </label>
                    <input
                      type="number"
                      min={1}
                      max={500}
                      value={queueSettings.min_per_minute || 40}
                      onChange={(e) => {
                        const val = Number(e.target.value);
                        setQueueSettings((prev) => ({
                          ...prev,
                          min_per_minute: val,
                        }));
                      }}
                      className="w-full px-3.5 py-2.5 text-xs bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-indigo-500 font-mono"
                    />
                    <p className="text-[11px] text-gray-400 mt-1">Floor rate during low-load intervals</p>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                      Messages Per Minute – MAX (Ceiling Rate)
                    </label>
                    <input
                      type="number"
                      min={1}
                      max={1000}
                      value={queueSettings.max_per_minute || 60}
                      onChange={(e) => {
                        const val = Number(e.target.value);
                        setQueueSettings((prev) => ({
                          ...prev,
                          max_per_minute: val,
                        }));
                      }}
                      className="w-full px-3.5 py-2.5 text-xs bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-indigo-500 font-mono"
                    />
                    <p className="text-[11px] text-gray-400 mt-1">Ceiling rate to prevent carrier/Meta throttling</p>
                  </div>
                </div>

                {/* Dispatch Status and Mode */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                      Queue Dispatch Status
                    </label>
                    <select
                      value={queueSettings.queue_status || "ACTIVE"}
                      onChange={(e) => {
                        setQueueSettings((prev) => ({
                          ...prev,
                          queue_status: e.target.value,
                        }));
                      }}
                      className="w-full px-3.5 py-2.5 text-xs bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-indigo-500 font-semibold"
                    >
                      <option value="ACTIVE">ACTIVE (Accept & Dispatch Messages)</option>
                      <option value="PAUSED">PAUSED (Hold In Queue Without Discarding)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                      Throttle Distribution Mode
                    </label>
                    <select
                      value={queueSettings.worker_config?.str_worker_mode || "RANGE"}
                      onChange={(e) => {
                        setQueueSettings((prev) => ({
                          ...prev,
                          worker_config: {
                            ...(prev.worker_config || {}),
                            str_worker_mode: e.target.value,
                          },
                        }));
                      }}
                      className="w-full px-3.5 py-2.5 text-xs bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-indigo-500 font-semibold"
                    >
                      <option value="RANGE">
                        Organic Dynamic Range ({queueSettings.min_per_minute || 40}–{queueSettings.max_per_minute || 60}/min)
                      </option>
                      <option value="FIXED">
                        Fixed Rate Limit ({queueSettings.per_minute_limit || 50}/min)
                      </option>
                    </select>
                  </div>
                </div>

              </div>
            )}

            {/* ══════════════════════════════════════════════════════════
                TAB 3: WORKER CONFIGURATION & PERFORMANCE ENGINE
            ══════════════════════════════════════════════════════════ */}
            {activeTab === "worker" && (
              <div className="space-y-6">
                
                <div className="p-4 bg-blue-50/70 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800/60 rounded-2xl text-xs text-blue-800 dark:text-blue-300 leading-relaxed">
                  ⚙️ <strong>Dynamic Worker Performance Engine:</strong> Parameters are calculated dynamically per user from user messaging limits, selected sender numbers, campaign batches, and queue depth. They are strictly isolated per tenant.
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5">
                  <div className="p-4 rounded-2xl bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">QUEUE_NAME</span>
                    <div className="text-sm font-black font-mono text-indigo-600 dark:text-indigo-400 mt-1">
                      {queueSettings.queue_name}
                    </div>
                    <p className="text-[11px] text-gray-400 mt-1">Belongs exclusively to this tenant.</p>
                  </div>

                  <div className="p-4 rounded-2xl bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">BATCH_SIZE</span>
                    <div className="text-sm font-black font-mono text-gray-900 dark:text-white mt-1">
                      {queueSettings.worker_performance_config?.BATCH_SIZE || 100} msgs
                    </div>
                    <p className="text-[11px] text-gray-400 mt-1">Messages processed per batch chunk.</p>
                  </div>

                  <div className="p-4 rounded-2xl bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">CONCURRENT_WORKERS</span>
                    <div className="text-sm font-black font-mono text-blue-600 dark:text-blue-400 mt-1">
                      {queueSettings.worker_performance_config?.CONCURRENT_WORKERS || queueSettings.active_workers || 3} workers
                    </div>
                    <p className="text-[11px] text-gray-400 mt-1">Parallel processes allocated for this user.</p>
                  </div>

                  <div className="p-4 rounded-2xl bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">RATE_LIMIT_DELAY</span>
                    <div className="text-sm font-black font-mono text-purple-600 dark:text-purple-400 mt-1">
                      {queueSettings.worker_performance_config?.RATE_LIMIT_DELAY || 1000} ms
                    </div>
                    <p className="text-[11px] text-gray-400 mt-1">Dynamically computed from sending rate.</p>
                  </div>

                  <div className="p-4 rounded-2xl bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">WORKER_PREFETCH</span>
                    <div className="text-sm font-black font-mono text-emerald-600 dark:text-emerald-400 mt-1">
                      {queueSettings.worker_performance_config?.WORKER_PREFETCH || 10} msgs
                    </div>
                    <p className="text-[11px] text-gray-400 mt-1">RabbitMQ prefetch buffer size.</p>
                  </div>

                  <div className="p-4 rounded-2xl bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">MAX_IN_FLIGHT_MESSAGES</span>
                    <div className="text-sm font-black font-mono text-amber-600 dark:text-amber-400 mt-1">
                      {queueSettings.worker_performance_config?.MAX_IN_FLIGHT_MESSAGES || 30} msgs
                    </div>
                    <p className="text-[11px] text-gray-400 mt-1">Concurrency × Prefetch ceiling.</p>
                  </div>

                  <div className="p-4 rounded-2xl bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">MESSAGES PER MINUTE (RANGE)</span>
                    <div className="text-sm font-black font-mono text-gray-900 dark:text-white mt-1">
                      {queueSettings.min_per_minute || 40} – {queueSettings.max_per_minute || 60} /min
                    </div>
                    <p className="text-[11px] text-gray-400 mt-1">Floor and ceiling speed bounds.</p>
                  </div>

                  <div className="p-4 rounded-2xl bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">RETRY_ATTEMPTS & BACKOFF</span>
                    <div className="text-sm font-black font-mono text-gray-900 dark:text-white mt-1">
                      {queueSettings.worker_performance_config?.RETRY_ATTEMPTS || 3} attempts · {queueSettings.worker_performance_config?.RETRY_BACKOFF || "EXPONENTIAL"}
                    </div>
                    <p className="text-[11px] text-gray-400 mt-1">Exponential backoff on carrier timeouts.</p>
                  </div>

                  <div className="p-4 rounded-2xl bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">COOLING_OFF_PERIOD</span>
                    <div className="text-sm font-black font-mono text-gray-900 dark:text-white mt-1">
                      {queueSettings.worker_performance_config?.COOLING_OFF_PERIOD || 5000} ms
                    </div>
                    <p className="text-[11px] text-gray-400 mt-1">Pause period between campaign batch chunks.</p>
                  </div>
                </div>

                {/* Dynamic Scaling Rules Box */}
                <div className="p-4 rounded-2xl bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 text-xs space-y-2">
                  <strong className="text-gray-900 dark:text-white block font-bold">
                    📈 Dynamic Concurrency Scaling Rules:
                  </strong>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 font-mono text-[11px]">
                    <div className="p-3 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                      Queue &le; 50 msgs &rarr; <strong className="text-indigo-600 dark:text-indigo-400">1 worker</strong>
                    </div>
                    <div className="p-3 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                      Queue &le; 500 msgs &rarr; <strong className="text-blue-600 dark:text-blue-400">3 workers</strong>
                    </div>
                    <div className="p-3 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                      Queue &gt; 500 msgs &rarr; <strong className="text-emerald-600 dark:text-emerald-400">5 workers</strong>
                    </div>
                  </div>
                </div>

              </div>
            )}

            {/* ══════════════════════════════════════════════════════════
                TAB 4: ATOMIC QUOTA & USAGE
            ══════════════════════════════════════════════════════════ */}
            {activeTab === "usage" && (
              <div className="space-y-6">
                
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3.5 text-center font-mono">
                  <div className="p-4 rounded-2xl bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700">
                    <span className="text-[10px] text-gray-400 font-sans block uppercase">Messages Today</span>
                    <p className="text-2xl font-black text-gray-900 dark:text-white mt-1">
                      {Number(queueSettings.messages_today || 0).toLocaleString()}
                    </p>
                  </div>

                  <div className="p-4 rounded-2xl bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700">
                    <span className="text-[10px] text-gray-400 font-sans block uppercase">Last 24 Hours</span>
                    <p className="text-2xl font-black text-blue-600 dark:text-blue-400 mt-1">
                      {Number(queueSettings.used_24h || 0).toLocaleString()}
                    </p>
                  </div>

                  <div className="p-4 rounded-2xl bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700">
                    <span className="text-[10px] text-gray-400 font-sans block uppercase">Remaining Quota</span>
                    <p className="text-2xl font-black text-emerald-600 dark:text-emerald-400 mt-1">
                      {queueSettings.is_unlimited ? "∞" : Number(queueSettings.remaining_24h || 0).toLocaleString()}
                    </p>
                  </div>

                  <div className="p-4 rounded-2xl bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700">
                    <span className="text-[10px] text-gray-400 font-sans block uppercase">Quota Consumed</span>
                    <p className="text-2xl font-black text-indigo-600 dark:text-indigo-400 mt-1">
                      {queueSettings.is_unlimited ? "0%" : `${queueSettings.usage_percent}%`}
                    </p>
                  </div>
                </div>

                <div className="p-4 bg-emerald-50/70 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800/60 rounded-2xl text-xs text-emerald-900 dark:text-emerald-300 space-y-1.5">
                  <strong>🛡️ Concurrency Safety Guarantee:</strong>
                  <p className="leading-relaxed">
                    When multiple workers process batches in parallel, <code className="font-mono bg-emerald-100 dark:bg-emerald-900/60 px-1.5 py-0.5 rounded">reserveQuota(userId, requestedCount)</code> performs an atomic decrement ensuring <code className="font-mono">remaining &ge; requestedCount</code> before jobs dispatch. If only 30 messages remain, exactly 30 are reserved. <strong>Quota overrun is physically impossible.</strong>
                  </p>
                </div>

                <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                  ⚡ <strong>Rolling Window Counter:</strong> High-speed 24-hour timestamped tracking eliminates expensive full-table <code className="font-mono text-gray-700 dark:text-gray-300">COUNT(*)</code> operations, supporting multi-million message volume with zero latency.
                </p>

              </div>
            )}

            {/* ══════════════════════════════════════════════════════════
                TAB 5: GENERAL & TENANT IDENTITY
            ══════════════════════════════════════════════════════════ */}
            {activeTab === "general" && (
              <div className="space-y-6">
                
                <div className="p-5 rounded-2xl bg-gray-50/80 dark:bg-gray-900/60 border border-gray-200 dark:border-gray-700 space-y-4">
                  <span className="text-xs font-bold uppercase tracking-wider text-gray-400">
                    Tenant Identification & Database Scope
                  </span>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-xs">
                    <div>
                      <span className="text-gray-400 block text-[11px]">Full Name</span>
                      <strong className="text-gray-900 dark:text-white font-semibold text-sm">
                        {targetUser?.str_name || "—"}
                      </strong>
                    </div>

                    <div>
                      <span className="text-gray-400 block text-[11px]">Username</span>
                      <code className="text-indigo-600 dark:text-indigo-400 font-mono text-sm font-bold">
                        @{targetUser?.str_username}
                      </code>
                    </div>

                    <div>
                      <span className="text-gray-400 block text-[11px]">User ID</span>
                      <code className="text-gray-700 dark:text-gray-300 font-mono">
                        {targetUser?._id}
                      </code>
                    </div>

                    <div>
                      <span className="text-gray-400 block text-[11px]">Account / Company</span>
                      <strong className="text-gray-900 dark:text-white font-semibold text-sm">
                        {userAccount?.str_company_name || targetUser?.str_account_id || "Platform"}
                      </strong>
                    </div>

                    <div>
                      <span className="text-gray-400 block text-[11px]">Assigned Role</span>
                      <span className="font-semibold text-gray-700 dark:text-gray-300">
                        {userRole?.str_role_name || targetUser?.str_role_name || "User"}
                      </span>
                    </div>

                    <div>
                      <span className="text-gray-400 block text-[11px]">Tenant Database</span>
                      <code className="text-emerald-600 dark:text-emerald-400 font-mono">
                        digi_user_{targetUser?._id}
                      </code>
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 rounded-2xl text-xs text-gray-600 dark:text-gray-400 space-y-2">
                  <strong className="text-gray-800 dark:text-gray-200 block">
                    🛡️ One Queue Per User Architecture:
                  </strong>
                  <p className="leading-relaxed">
                    RabbitMQ assigns this tenant a personal, durable message queue. There is zero cross-user worker consumption. Tenant scaling, pausing, draining, and rate limits operate completely isolated from other users on the platform.
                  </p>
                </div>

              </div>
            )}
          </>
        )}
      </div>

      {/* ── Page Footer Action Strip ── */}
      <div className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-xs">
        <button
          type="button"
          onClick={() => navigate("/admin/users")}
          className="px-4 py-2 text-xs font-semibold rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
        >
          ← Back to Users List
        </button>

        <button
          type="button"
          onClick={handleSaveQueueSettings}
          disabled={saving || loading || !targetUser}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 text-white text-xs font-bold shadow-md shadow-indigo-500/20 transition-all cursor-pointer disabled:opacity-50"
        >
          {saving ? (
            <>
              <ArrowPathIcon className="w-4 h-4 animate-spin" />
              <span>Saving Queue Settings...</span>
            </>
          ) : (
            <>
              <CheckCircleIcon className="w-4 h-4" />
              <span>Save Queue Settings</span>
            </>
          )}
        </button>
      </div>

    </div>
  );
}
